/**
 * DOM UI: menu, HUD, pause, completion, settings, how-to-play.
 * Purely observational + callback-driven; owns no gameplay state.
 */
import {
  QUALITY_PRESETS,
  detectQualityPreset,
  type Settings,
  type QualityPreset,
} from '../game/settings';
import type { LevelProgress } from '../game/save';
import {
  COSMETICS,
  ACHIEVEMENTS,
  CONTRACTS,
  CIRCUITS,
  VAULT_TIERS,
  cosmeticUnlocked,
  cacheStatus,
  contractProgress,
  circuitProgress,
  type Profile,
  type CompletionAward,
} from '../game/progression';
import { t } from './strings';
import { isEditableTarget } from '../input/input';

export interface UiCallbacks {
  onPlay(levelIndex: number): void;
  onOpenEditor(): void;
  /** Garage equip: kind is the equipped slot, id the cosmetic. */
  onEquip(slot: 'primary' | 'secondary' | 'core' | 'trail' | 'burst' | 'shell', id: string): void;
  /** Fresh profile for the garage/profile screens. */
  getProfile(): Profile;
  /** Shop purchase; returns success. */
  onBuy(id: string): boolean;
  onClaimCache(): { flux: number; prisms: number } | null;
  onClaimContract(id: string): { flux: number; prisms: number } | null;
  onClaimCircuit(id: string): { flux: number; prisms: number } | null;
  /** Vault tier claim; returns the granted cosmetic label or null. */
  onClaimVault(id: string): string | null;
  /** Open the online panel (account/browser/boards). */
  onOpenOnline(): void;
  /** Open the admin console (button only exists for role=admin). */
  onOpenAdmin(): void;
  /** Publish the just-completed playtest level online. */
  onPublish(): void;
  /** Login gate: resolve to an error message, or null on success. */
  onGateLogin(username: string, password: string): Promise<string | null>;
  onGateRegister(
    username: string,
    password: string,
    question: string,
    answer: string,
  ): Promise<string | null>;
  /** Lost-password step 1: fetch the account's security question. */
  onGateQuestion(username: string): Promise<{ question: string } | { error: string }>;
  /** Lost-password step 2: check the typed answer. */
  onGateVerify(username: string, answer: string): Promise<string | null>;
  /** Lost-password step 3: set the new password. */
  onGateReset(username: string, answer: string, password: string): Promise<string | null>;
  /** Play without an account (only offered when servers are unreachable). */
  onGateOffline(): void;
  onLogout(): void;
  onExportSave(): void;
  onImportSave(): void;
  onPause(): void;
  onResume(): void;
  onRestart(): void;
  onExitToMenu(): void;
  onTogglePractice(): void;
  onSettingsChanged(s: Settings): void;
  /** Platformer touch controls: pressed-state changes. */
  onTouchMove(dir: 'left' | 'right', down: boolean): void;
  /** Calibration: schedule metronome clicks, returns their clock times. */
  onMetronomeStart(bpm: number, beats: number): number[];
  /** Current audio clock, seconds. */
  onAudioNow(): number;
}

export class Ui {
  private root: HTMLElement;
  private cb: UiCallbacks;
  private settings: Settings;

  private menuEl!: HTMLElement;
  private hudEl!: HTMLElement;
  private pauseEl!: HTMLElement;
  private completeEl!: HTMLElement;
  private settingsEl!: HTMLElement;
  private howtoEl!: HTMLElement;
  private progressBar!: HTMLElement;
  private progressBest!: HTMLElement;
  private progressText!: HTMLElement;
  private attemptLabel!: HTMLElement;
  private attemptTimer: number | undefined;
  private lastLevelIndex = 0;

  constructor(root: HTMLElement, settings: Settings, cb: UiCallbacks) {
    this.root = root;
    this.cb = cb;
    this.settings = settings;
    this.build();
  }

  private build(): void {
    this.root.innerHTML = `
      <section id="login-gate" class="screen hidden">
        <div class="logo">Shape Shifters</div>
        <div class="tagline">${t('tagline')}</div>
        <div class="panel login-panel" id="gate-panel"></div>
      </section>

      <section id="menu" class="screen hidden">
        <div class="logo">Shape Shifters</div>
        <div class="tagline">${t('tagline')}</div>
        <div id="menu-account" class="hidden">
          <span id="menu-account-name"></span>
          <button id="btn-logout" class="small">${t('online.logout')}</button>
        </div>
        <div id="level-list"></div>
        <button id="btn-editor">${t('menu.editor')}</button>
        <button id="btn-garage">${t('menu.garage')}</button>
        <button id="btn-online">${t('menu.online')}</button>
        <button id="btn-admin" class="hidden admin-btn">${t('menu.admin')}</button>
        <button id="btn-howto">${t('menu.howto')}</button>
        <button id="btn-settings">${t('menu.settings')}</button>
        <button id="btn-vault" class="vault-glyph" aria-label="◈">◈</button>
      </section>

      <section id="vault" class="screen overlay hidden">
        <div class="panel" id="vault-panel"></div>
      </section>

      <section id="online" class="screen overlay hidden">
        <div class="panel" id="online-panel"></div>
      </section>

      <section id="admin" class="screen overlay hidden">
        <div class="panel" id="admin-panel"></div>
      </section>

      <section id="hud" class="hidden">
        <div id="progress-wrap">
          <div id="progress-bar"></div>
          <div id="progress-best"></div>
          <span id="progress-text">0%</span>
        </div>
        <div id="attempt-label"></div>
        <div id="practice-badge" class="hidden">${t('hud.practice')}</div>
        <div id="testing-badge" class="hidden">${t('hud.testing')}</div>
        <div id="platformer-stats" class="hidden"><span id="pf-timer">0.0s</span> · <span id="pf-deaths">0 ${t('hud.deaths')}</span></div>
        <div id="touch-controls" class="hidden">
          <button id="touch-left" aria-label="${t('hud.move-left')}">◀</button>
          <button id="touch-right" aria-label="${t('hud.move-right')}">▶</button>
        </div>
        <button id="pause-btn" aria-label="${t('hud.pause')}">⏸</button>
      </section>

      <section id="pause" class="screen overlay hidden">
        <h2>${t('pause.title')}</h2>
        <button id="btn-resume" class="primary">${t('pause.resume')}</button>
        <button id="btn-restart">${t('pause.restart')}</button>
        <button id="btn-practice">${t('pause.practice-off')}</button>
        <button id="btn-pause-settings">${t('menu.settings')}</button>
        <button id="btn-exit">${t('pause.exit')}</button>
        <p class="hint">${t('pause.hint')}</p>
      </section>

      <section id="complete" class="screen overlay hidden">
        <h2 class="win-title">${t('complete.title')}</h2>
        <div class="panel" id="complete-stats"></div>
        <button id="btn-again" class="primary">${t('complete.again')}</button>
        <button id="btn-complete-exit">${t('pause.exit')}</button>
      </section>

      <section id="settings-screen" class="screen overlay hidden">
        <div class="panel">
          <h2>${t('settings.title')}</h2>
          <div class="row"><label for="set-music">${t('settings.music')}</label>
            <input id="set-music" type="range" min="0" max="1" step="0.05"></div>
          <div class="row"><label for="set-sfx">${t('settings.sfx')}</label>
            <input id="set-sfx" type="range" min="0" max="1" step="0.05"></div>
          <div class="row"><label for="set-offset">${t('settings.offset')}</label>
            <input id="set-offset" type="range" min="-250" max="250" step="5"></div>
          <div class="row"><label for="set-glow">${t('settings.glow')}</label>
            <input id="set-glow" type="checkbox"></div>
          <div class="row"><label for="set-particles">${t('settings.particles')}</label>
            <input id="set-particles" type="checkbox"></div>
          <div class="row"><label for="set-flash">${t('settings.flash')}</label>
            <input id="set-flash" type="checkbox"></div>
          <div class="row"><label for="set-motion">${t('settings.motion')}</label>
            <input id="set-motion" type="checkbox"></div>
          <div class="row"><label for="set-ldm">${t('settings.ldm')}</label>
            <input id="set-ldm" type="checkbox"></div>
          <div class="row"><label for="set-preset">${t('settings.preset')}</label>
            <select id="set-preset">
              <option value="">—</option>
              <option value="auto">${t('settings.preset.auto')}</option>
              <option value="low">${t('settings.preset.low')}</option>
              <option value="medium">${t('settings.preset.medium')}</option>
              <option value="high">${t('settings.preset.high')}</option>
            </select></div>
          <div class="row"><label for="set-uiscale">${t('settings.uiscale')}</label>
            <input id="set-uiscale" type="range" min="0.8" max="1.5" step="0.05"></div>
          <div class="row"><label for="set-contrast">${t('settings.contrast')}</label>
            <input id="set-contrast" type="checkbox"></div>
          <div class="row"><label for="set-cb">${t('settings.colorblind')}</label>
            <input id="set-cb" type="checkbox"></div>
          <div class="row"><label>${t('settings.key-jump')}</label>
            <button id="set-key-jump" class="small keycap"></button></div>
          <div class="row"><label>${t('settings.key-left')}</label>
            <button id="set-key-left" class="small keycap"></button></div>
          <div class="row"><label>${t('settings.key-right')}</label>
            <button id="set-key-right" class="small keycap"></button></div>
          <div class="row"><label>${t('settings.pad-jump')}</label>
            <button id="set-pad-jump" class="small keycap"></button></div>
          <div class="row"><label for="set-fpscap">${t('settings.fpscap')}</label>
            <select id="set-fpscap">
              <option value="0">${t('settings.fpscap.off')}</option>
              <option value="60">60</option>
              <option value="30">30</option>
            </select></div>
          <div class="row"><label for="set-renderscale">${t('settings.renderscale')}</label>
            <select id="set-renderscale">
              <option value="1">100%</option>
              <option value="0.75">75%</option>
              <option value="0.5">50%</option>
            </select></div>
          <div class="row">
            <button id="btn-fullscreen" class="small">${t('settings.fullscreen')}</button>
            <button id="btn-calibrate" class="small">${t('settings.calibrate')}</button>
          </div>
          <div class="row">
            <button id="btn-export-save" class="small">${t('settings.export')}</button>
            <button id="btn-import-save" class="small">${t('settings.import')}</button>
          </div>
          <div id="calibrate-host"></div>
          <button id="btn-settings-close" class="small">${t('settings.done')}</button>
        </div>
      </section>

      <section id="garage" class="screen overlay hidden">
        <div class="panel" id="garage-panel"></div>
      </section>

      <section id="howto" class="screen overlay hidden">
        <div class="panel">
          <h2>${t('howto.title')}</h2>
          <p class="hint">${t('howto.body')}</p>
          <button id="btn-howto-close" class="small">${t('howto.close')}</button>
        </div>
      </section>
    `;

    this.menuEl = this.get('#menu');
    this.hudEl = this.get('#hud');
    this.pauseEl = this.get('#pause');
    this.completeEl = this.get('#complete');
    this.settingsEl = this.get('#settings-screen');
    this.howtoEl = this.get('#howto');
    this.progressBar = this.get('#progress-bar');
    this.progressBest = this.get('#progress-best');
    this.progressText = this.get('#progress-text');
    this.attemptLabel = this.get('#attempt-label');

    this.get('#btn-resume').addEventListener('click', () => this.cb.onResume());
    this.get('#btn-restart').addEventListener('click', () => this.cb.onRestart());
    this.get('#btn-exit').addEventListener('click', () => this.cb.onExitToMenu());
    this.get('#btn-practice').addEventListener('click', () => this.cb.onTogglePractice());
    this.get('#btn-editor').addEventListener('click', () => this.cb.onOpenEditor());
    this.get('#btn-garage').addEventListener('click', () => this.showGarage());
    this.get('#btn-vault').addEventListener('click', () => this.showVault());
    this.get('#btn-online').addEventListener('click', () => this.cb.onOpenOnline());
    this.get('#btn-admin').addEventListener('click', () => this.cb.onOpenAdmin());
    this.get('#btn-logout').addEventListener('click', () => this.cb.onLogout());
    this.get('#btn-again').addEventListener('click', () => this.cb.onPlay(this.lastLevelIndex));
    this.get('#btn-complete-exit').addEventListener('click', () => this.cb.onExitToMenu());
    this.get('#pause-btn').addEventListener('click', () => this.cb.onPause());
    this.get('#btn-howto').addEventListener('click', () => this.howtoEl.classList.remove('hidden'));
    this.get('#btn-howto-close').addEventListener('click', () =>
      this.howtoEl.classList.add('hidden'),
    );
    this.get('#btn-settings').addEventListener('click', () => this.openSettings());
    this.get('#btn-pause-settings').addEventListener('click', () => this.openSettings());
    this.get('#btn-settings-close').addEventListener('click', () =>
      this.settingsEl.classList.add('hidden'),
    );
    this.get('#btn-export-save').addEventListener('click', () => this.cb.onExportSave());
    this.get('#btn-import-save').addEventListener('click', () => this.cb.onImportSave());

    // Touch move buttons: pointer down/up drive held state (platformer).
    for (const [id, dir] of [
      ['#touch-left', 'left'],
      ['#touch-right', 'right'],
    ] as const) {
      const el = this.get(id);
      const down = (e: Event): void => {
        e.preventDefault();
        this.cb.onTouchMove(dir, true);
      };
      const up = (): void => this.cb.onTouchMove(dir, false);
      el.addEventListener('pointerdown', down);
      el.addEventListener('pointerup', up);
      el.addEventListener('pointercancel', up);
      el.addEventListener('pointerleave', up);
    }

    this.bindSettings();
  }

  /** Switch HUD between normal (progress %) and platformer (timer/deaths). */
  setMode(mode: 'normal' | 'platformer'): void {
    const isPf = mode === 'platformer';
    this.get('#progress-wrap').classList.toggle('hidden', isPf);
    this.get('#platformer-stats').classList.toggle('hidden', !isPf);
    const touch = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
    this.get('#touch-controls').classList.toggle('hidden', !(isPf && touch));
  }

  updatePlatformerStats(timeS: number, deaths: number): void {
    this.get('#pf-timer').textContent = `${timeS.toFixed(1)}s`;
    this.get('#pf-deaths').textContent =
      `${deaths} ${deaths === 1 ? t('hud.death') : t('hud.deaths')}`;
  }

  private get(sel: string): HTMLElement {
    const el = this.root.querySelector<HTMLElement>(sel);
    if (!el) throw new Error(`UI element missing: ${sel}`);
    return el;
  }

  private input(sel: string): HTMLInputElement {
    return this.get(sel) as HTMLInputElement;
  }

  private bindSettings(): void {
    const s = this.settings;
    this.input('#set-music').value = String(s.musicVolume);
    this.input('#set-sfx').value = String(s.sfxVolume);
    this.input('#set-offset').value = String(s.audioOffsetMs);
    this.input('#set-glow').checked = s.glow;
    this.input('#set-particles').checked = s.particles;
    this.input('#set-flash').checked = s.reducedFlashing;
    this.input('#set-motion').checked = s.reducedMotion;
    this.input('#set-ldm').checked = s.lowDetail;
    this.input('#set-uiscale').value = String(s.uiScale);
    this.input('#set-contrast').checked = s.highContrast;
    this.input('#set-cb').checked = s.colorblind;
    (this.get('#set-fpscap') as HTMLSelectElement).value = String(s.fpsCap);
    (this.get('#set-renderscale') as HTMLSelectElement).value = String(s.renderScale);
    this.refreshKeycaps();

    const emit = (): void => {
      this.settings = {
        ...this.settings,
        musicVolume: Number(this.input('#set-music').value),
        sfxVolume: Number(this.input('#set-sfx').value),
        audioOffsetMs: Number(this.input('#set-offset').value),
        glow: this.input('#set-glow').checked,
        particles: this.input('#set-particles').checked,
        reducedFlashing: this.input('#set-flash').checked,
        reducedMotion: this.input('#set-motion').checked,
        lowDetail: this.input('#set-ldm').checked,
        uiScale: Number(this.input('#set-uiscale').value),
        highContrast: this.input('#set-contrast').checked,
        colorblind: this.input('#set-cb').checked,
        fpsCap: Number((this.get('#set-fpscap') as HTMLSelectElement).value),
        renderScale: Number((this.get('#set-renderscale') as HTMLSelectElement).value),
      };
      this.cb.onSettingsChanged(this.settings);
    };
    for (const id of [
      '#set-music',
      '#set-sfx',
      '#set-offset',
      '#set-glow',
      '#set-particles',
      '#set-flash',
      '#set-motion',
      '#set-ldm',
      '#set-uiscale',
      '#set-contrast',
      '#set-cb',
    ]) {
      this.get(id).addEventListener('input', emit);
    }
    this.get('#set-fpscap').addEventListener('change', emit);
    this.get('#set-renderscale').addEventListener('change', emit);

    // Quality preset select maps onto the individual toggles.
    (this.get('#set-preset') as HTMLSelectElement).addEventListener('change', (e) => {
      const value = (e.target as HTMLSelectElement).value;
      if (!value) return;
      const preset = value === 'auto' ? detectQualityPreset() : (value as QualityPreset);
      const q = QUALITY_PRESETS[preset];
      this.input('#set-glow').checked = q.glow;
      this.input('#set-particles').checked = q.particles;
      this.input('#set-ldm').checked = q.lowDetail;
      emit();
    });

    // Key rebinding: click, then the next key pressed becomes the binding.
    const capture = (btnSel: string, field: 'keyJump' | 'keyLeft' | 'keyRight'): void => {
      this.get(btnSel).addEventListener('click', () => {
        this.get(btnSel).textContent = t('settings.key-listen');
        const onKey = (e: KeyboardEvent): void => {
          e.preventDefault();
          window.removeEventListener('keydown', onKey, true);
          if (e.code !== 'Escape') {
            this.settings = { ...this.settings, [field]: [e.code] };
            this.cb.onSettingsChanged(this.settings);
          }
          this.refreshKeycaps();
        };
        window.addEventListener('keydown', onKey, true);
      });
    };
    capture('#set-key-jump', 'keyJump');
    capture('#set-key-left', 'keyLeft');
    capture('#set-key-right', 'keyRight');

    // Gamepad rebinding: poll for the next freshly pressed button (5 s window).
    this.get('#set-pad-jump').addEventListener('click', () => {
      this.get('#set-pad-jump').textContent = t('settings.pad-listen');
      const initial = new Set<string>();
      for (const pad of navigator.getGamepads?.() ?? []) {
        pad?.buttons.forEach((b, i) => {
          if (b.pressed) initial.add(`${pad.index}:${i}`);
        });
      }
      const startedAt = performance.now();
      const poll = (): void => {
        if (performance.now() - startedAt > 5000) {
          this.refreshKeycaps();
          return;
        }
        for (const pad of navigator.getGamepads?.() ?? []) {
          if (!pad) continue;
          for (let i = 0; i < pad.buttons.length; i++) {
            if (pad.buttons[i]?.pressed && !initial.has(`${pad.index}:${i}`)) {
              this.settings = { ...this.settings, padJump: [i] };
              this.cb.onSettingsChanged(this.settings);
              this.refreshKeycaps();
              return;
            }
          }
        }
        requestAnimationFrame(poll);
      };
      requestAnimationFrame(poll);
    });

    this.get('#btn-fullscreen').addEventListener('click', () => {
      if (document.fullscreenElement) void document.exitFullscreen();
      else void document.documentElement.requestFullscreen();
    });
    this.get('#btn-calibrate').addEventListener('click', () => this.runCalibration());
  }

  private refreshKeycaps(): void {
    this.get('#set-key-jump').textContent = this.settings.keyJump.join(' / ');
    this.get('#set-key-left').textContent = this.settings.keyLeft.join(' / ');
    this.get('#set-key-right').textContent = this.settings.keyRight.join(' / ');
    this.get('#set-pad-jump').textContent = this.settings.padJump.map((b) => `B${b}`).join(' / ');
  }

  /** Tap-to-beat latency wizard: median (tap − click) time → audio offset. */
  private runCalibration(): void {
    const host = this.get('#calibrate-host');
    const BEATS = 12;
    const clickTimes = this.cb.onMetronomeStart(120, BEATS);
    if (clickTimes.length === 0) return; // audio unavailable (never unlocked)
    const taps: number[] = [];
    host.innerHTML = `<div class="ed-cat">${t('calibrate.title')}</div>
      <p class="hint">${t('calibrate.body')}</p>
      <b id="cal-progress">${t('calibrate.progress', { n: 0, total: BEATS })}</b>
      <button class="small" id="cal-cancel">${t('calibrate.cancel')}</button>`;
    const finish = (completed: boolean): void => {
      window.removeEventListener('keydown', onTap, true);
      window.removeEventListener('pointerdown', onTap, true);
      if (!completed) {
        host.innerHTML = '';
        return;
      }
      // Offset per tap = tap time − nearest click time; use the median and
      // ignore the first two taps (players settle into the beat).
      const offsets = taps.slice(2).map((tap) => {
        let best = Number.POSITIVE_INFINITY;
        for (const c of clickTimes) if (Math.abs(tap - c) < Math.abs(best)) best = tap - c;
        return best * 1000;
      });
      offsets.sort((a, b) => a - b);
      const median = offsets[Math.floor(offsets.length / 2)] ?? 0;
      const ms = Math.max(-250, Math.min(250, Math.round(median)));
      this.settings = { ...this.settings, audioOffsetMs: ms };
      this.input('#set-offset').value = String(ms);
      this.cb.onSettingsChanged(this.settings);
      host.innerHTML = `<b>${t('calibrate.result', { ms })}</b>`;
    };
    const onTap = (e: Event): void => {
      if ((e.target as HTMLElement).id === 'cal-cancel') return;
      e.preventDefault();
      taps.push(this.cb.onAudioNow());
      const el = host.querySelector('#cal-progress');
      if (el) el.textContent = t('calibrate.progress', { n: taps.length, total: BEATS });
      if (taps.length >= BEATS) finish(true);
    };
    window.addEventListener('keydown', onTap, true);
    window.addEventListener('pointerdown', onTap, true);
    host.querySelector('#cal-cancel')?.addEventListener('click', () => finish(false));
  }

  private openSettings(): void {
    this.settingsEl.classList.remove('hidden');
  }

  // --- Screen switching ------------------------------------------------------

  /** Index of the level currently fronted in the menu carousel. */
  private menuIndex = Math.max(0, Number(localStorage.getItem('shape-shifters.menu-index') ?? 0));
  private menuCards: Array<{ name: string; subtitle: string }> = [];

  private pageMenu(delta: number): void {
    if (this.menuCards.length === 0) return;
    // Wrap around, GD-style.
    this.menuIndex = (this.menuIndex + delta + this.menuCards.length) % this.menuCards.length;
    localStorage.setItem('shape-shifters.menu-index', String(this.menuIndex));
    this.showMenu(this.menuCards);
  }

  showMenu(cards: Array<{ name: string; subtitle: string }>): void {
    this.setVisible(this.menuEl, true);
    this.setVisible(this.hudEl, false);
    this.setVisible(this.pauseEl, false);
    this.setVisible(this.completeEl, false);
    this.menuCards = cards;
    const list = this.get('#level-list');
    if (cards.length === 0) {
      list.innerHTML = '';
      return;
    }
    this.menuIndex = Math.min(Math.max(0, this.menuIndex), cards.length - 1);
    const card = cards[this.menuIndex] as { name: string; subtitle: string };

    list.innerHTML = `
      <div class="level-carousel">
        <button class="carousel-arrow" id="level-prev" aria-label="${t('menu.prev')}">◀</button>
        <button class="level-card primary carousel-card" id="level-current">
          <span>${escapeHtml(card.name)}</span>
          <small>${escapeHtml(card.subtitle)}</small>
          <small class="carousel-pos">${this.menuIndex + 1} / ${cards.length}</small>
        </button>
        <button class="carousel-arrow" id="level-next" aria-label="${t('menu.next')}">▶</button>
      </div>
      <div class="carousel-dots">${cards
        .map((_, i) => `<span class="dot ${i === this.menuIndex ? 'active' : ''}"></span>`)
        .join('')}</div>`;

    this.get('#level-prev').addEventListener('click', () => this.pageMenu(-1));
    this.get('#level-next').addEventListener('click', () => this.pageMenu(1));
    const current = this.get('#level-current');
    let swiped = false;
    current.addEventListener('click', () => {
      if (swiped) return; // a swipe release is not a play tap
      this.lastLevelIndex = this.menuIndex;
      this.cb.onPlay(this.menuIndex);
    });
    // Swipe on the card flips pages; a short tap still plays.
    let downX: number | null = null;
    current.addEventListener('pointerdown', (e) => {
      downX = e.clientX;
      swiped = false;
    });
    current.addEventListener('pointerup', (e) => {
      if (downX !== null && Math.abs(e.clientX - downX) > 40) {
        swiped = true;
        this.pageMenu(e.clientX < downX ? 1 : -1);
      }
      downX = null;
    });
    // Arrow keys page while the menu is up (registered once).
    if (!this.menuKeyHandler) {
      this.menuKeyHandler = (e: KeyboardEvent): void => {
        if (this.menuEl.classList.contains('hidden')) return;
        if (isEditableTarget(e)) return; // typing in an overlay form
        if (e.code === 'ArrowLeft') this.pageMenu(-1);
        else if (e.code === 'ArrowRight') this.pageMenu(1);
      };
      window.addEventListener('keydown', this.menuKeyHandler);
    }
  }

  private menuKeyHandler: ((e: KeyboardEvent) => void) | null = null;

  showGame(): void {
    this.setVisible(this.menuEl, false);
    this.setVisible(this.hudEl, true);
    this.setVisible(this.pauseEl, false);
    this.setVisible(this.completeEl, false);
  }

  showPause(visible: boolean): void {
    this.setVisible(this.pauseEl, visible);
  }

  showComplete(
    levelName: string,
    attempts: number,
    jumps: number,
    timeS: number,
    p: LevelProgress,
    award: CompletionAward | null,
    canPublish = false,
  ): void {
    this.setVisible(this.completeEl, false);
    const stats = this.get('#complete-stats');
    let awardHtml = '';
    if (award) {
      const parts: string[] = [];
      if (award.sparks > 0) parts.push(`+${award.sparks} ✦ Sparks`);
      if (award.lumens > 0) parts.push(`+${award.lumens} ☾ Lumens`);
      if (award.flux > 0) parts.push(`+${award.flux} ⬡ Flux`);
      if (award.newShards > 0)
        parts.push(`+${award.newShards} shard${award.newShards > 1 ? 's' : ''}`);
      if (parts.length > 0) {
        awardHtml += `<div class="stat-line award-line"><span>${t('complete.rewards')}</span><b>${parts.join(' · ')}</b></div>`;
      }
      for (const a of award.achievements) {
        awardHtml += `<div class="stat-line award-line"><span>🏆 ${escapeHtml(a.label)}</span><b>+${a.rewardFlux} ⬡</b></div>`;
      }
    }
    stats.innerHTML = `
      <div class="stat-line"><span>${t('complete.level')}</span><b>${escapeHtml(levelName)}</b></div>
      <div class="stat-line"><span>${t('complete.attempts')}</span><b>${attempts}</b></div>
      <div class="stat-line"><span>${t('complete.jumps')}</span><b>${jumps}</b></div>
      <div class="stat-line"><span>${t('complete.time')}</span><b>${timeS.toFixed(1)}s</b></div>
      <div class="stat-line"><span>${t('complete.total')}</span><b>${p.completions}</b></div>
      ${awardHtml}
      ${canPublish ? `<button class="small" id="btn-publish">${t('online.publish')}</button>` : ''}
    `;
    stats.querySelector('#btn-publish')?.addEventListener('click', () => this.cb.onPublish());
    this.setVisible(this.completeEl, true);
  }

  /** Transient notice reusing the attempt-label flash (score sent, published…). */
  flashNotice(text: string): void {
    this.attemptLabel.textContent = text;
    this.attemptLabel.classList.add('show');
    window.clearTimeout(this.attemptTimer);
    this.attemptTimer = window.setTimeout(() => {
      this.attemptLabel.classList.remove('show');
    }, 3200);
  }

  updateHud(progress: number, best: number): void {
    const pct = Math.floor(progress * 100);
    this.progressBar.style.width = `${progress * 100}%`;
    this.progressBest.style.left = `${best * 100}%`;
    this.progressText.textContent = `${pct}%`;
  }

  /** Garage: cosmetics + achievements + lifetime stats, fully data-driven. */
  showGarage(): void {
    const p = this.cb.getProfile();
    const panel = this.get('#garage-panel');
    const swatches = (
      kind: 'color' | 'core' | 'trail' | 'burst' | 'shell',
      slot: 'primary' | 'secondary' | 'core' | 'trail' | 'burst' | 'shell',
    ): string =>
      COSMETICS.filter((c) => c.kind === kind)
        .map((c) => {
          const owned = cosmeticUnlocked(p, c);
          const equipped = p.equipped[slot] === c.id;
          const req = c.requires
            ? c.requires.sparks !== undefined
              ? `${c.requires.sparks}✦`
              : c.requires.flux !== undefined
                ? `${c.requires.flux}⬡`
                : '🏆'
            : '';
          const style = kind === 'color' ? `style="background:${c.value}"` : '';
          return `<button class="swatch ${kind} ${equipped ? 'equipped' : ''} ${owned ? '' : 'locked'}"
            data-slot="${slot}" data-id="${c.id}" ${style} title="${c.label}${owned ? '' : ` — ${t('garage.requires')} ${req}`}"
            ${owned ? '' : 'disabled'}>${kind === 'color' ? '' : c.label}${owned ? '' : `<span class="req">${req}</span>`}</button>`;
        })
        .join('');
    const shopItems = COSMETICS.filter((c) => c.price !== undefined)
      .map((c) => {
        const owned = p.unlocked.includes(c.id);
        const afford = p.flux >= (c.price ?? 0);
        return `<button class="swatch shop ${owned ? 'equipped' : ''}" data-buy="${c.id}"
          ${owned || !afford ? 'disabled' : ''} title="${c.label}">
          ${c.label}<span class="req">${owned ? t('garage.owned') : `${c.price}⬡`}</span></button>`;
      })
      .join('');
    const cache = cacheStatus(p);
    const cacheHtml = cache.ready
      ? `<button class="small" id="cache-claim">${t('garage.open-cache')}</button>`
      : `<span class="hint">${t('garage.next-cache', { min: Math.ceil(cache.remainingMs / 60000) })}</span>`;
    const contractsHtml = (p.contracts?.entries ?? [])
      .map((e) => {
        const def = CONTRACTS.find((c) => c.id === e.id);
        const prog = contractProgress(p, e.id);
        if (!def || !prog) return '';
        const doneAll = prog.done >= prog.target;
        const btn = e.claimed
          ? `<span class="hint">${t('garage.claimed')}</span>`
          : doneAll
            ? `<button class="small" data-contract="${e.id}">${t('garage.claim')} +${def.rewardFlux}⬡${def.rewardPrisms ? ` +${def.rewardPrisms}◈` : ''}</button>`
            : `<span class="hint">${prog.done}/${prog.target}</span>`;
        return `<div class="ach ${e.claimed ? 'done' : ''}"><b>${def.label}</b><span>${def.desc} — ${btn}</span></div>`;
      })
      .join('');
    const circuitsHtml = CIRCUITS.map((c) => {
      const prog = circuitProgress(c);
      const claimed = p.claimedCircuits.includes(c.id);
      const btn = claimed
        ? `<span class="hint">${t('garage.claimed')}</span>`
        : prog.done >= prog.total
          ? `<button class="small" data-circuit="${c.id}">${t('garage.claim')} +${c.rewardFlux}⬡ +${c.rewardPrisms}◈</button>`
          : `<span class="hint">${prog.done}/${prog.total} ${t('garage.levels')}</span>`;
      return `<div class="ach ${claimed ? 'done' : ''}"><b>${c.name}</b><span>${c.desc} — ${btn}</span></div>`;
    }).join('');
    const achievements = ACHIEVEMENTS.map((a) => {
      const done = p.achievements[a.id] !== undefined;
      return `<div class="ach ${done ? 'done' : ''}"><b>${a.label}</b><span>${a.desc} (+${a.rewardFlux}⬡)</span></div>`;
    }).join('');
    const st = p.stats;
    panel.innerHTML = `
      <h2>${t('garage.title')}</h2>
      <div class="stat-line"><span>✦ Sparks <b>${p.sparks}</b></span><span>☾ Lumens <b>${p.lumens}</b></span>
        <span>⬡ Flux <b>${p.flux}</b></span><span>◈ Prisms <b>${p.prisms}</b></span></div>
      <div class="ed-cat">${t('garage.primary')}</div><div class="swatch-row">${swatches('color', 'primary')}</div>
      <div class="ed-cat">${t('garage.secondary')}</div><div class="swatch-row">${swatches('color', 'secondary')}</div>
      <div class="ed-cat">${t('garage.core')}</div><div class="swatch-row">${swatches('core', 'core')}</div>
      <div class="ed-cat">${t('garage.trail')}</div><div class="swatch-row">${swatches('trail', 'trail')}</div>
      <div class="ed-cat">${t('garage.burst')}</div><div class="swatch-row">${swatches('burst', 'burst')}</div>
      <div class="ed-cat">${t('garage.shell')}</div><div class="swatch-row">${swatches('shell', 'shell')}</div>
      <div class="ed-cat">${t('garage.shop')}</div><div class="swatch-row">${shopItems}</div>
      <div class="ed-cat">${t('garage.cache')}</div><div>${cacheHtml}</div>
      <div class="ed-cat">${t('garage.contracts')}</div><div class="ach-list">${contractsHtml}</div>
      <div class="ed-cat">${t('garage.circuits')}</div><div class="ach-list">${circuitsHtml}</div>
      <div class="ed-cat">${t('garage.achievements')} (${Object.keys(p.achievements).length}/${ACHIEVEMENTS.length})</div>
      <div class="ach-list">${achievements}</div>
      <div class="ed-cat">${t('garage.stats')}</div>
      <div class="stat-line"><span>${t('garage.stat.jumps')}</span><b>${st.jumps}</b></div>
      <div class="stat-line"><span>${t('garage.stat.attempts')}</span><b>${st.attempts}</b></div>
      <div class="stat-line"><span>${t('garage.stat.completions')}</span><b>${st.completions}</b></div>
      <div class="stat-line"><span>${t('garage.stat.shards')}</span><b>${p.shards.length}</b></div>
      <div class="stat-line"><span>${t('garage.stat.playtime')}</span><b>${t('garage.stat.playtime-min', { min: (st.playTimeS / 60).toFixed(1) })}</b></div>
      <button class="small" id="garage-close">${t('garage.done')}</button>
    `;
    for (const btn of panel.querySelectorAll<HTMLButtonElement>(
      '.swatch:not(.locked):not(.shop)',
    )) {
      btn.addEventListener('click', () => {
        this.cb.onEquip(btn.dataset.slot as 'primary', btn.dataset.id as string);
        this.showGarage(); // re-render with new equip state
      });
    }
    for (const btn of panel.querySelectorAll<HTMLButtonElement>('[data-buy]')) {
      btn.addEventListener('click', () => {
        if (this.cb.onBuy(btn.dataset.buy as string)) this.showGarage();
      });
    }
    panel.querySelector('#cache-claim')?.addEventListener('click', () => {
      this.cb.onClaimCache();
      this.showGarage();
    });
    for (const btn of panel.querySelectorAll<HTMLButtonElement>('[data-contract]')) {
      btn.addEventListener('click', () => {
        this.cb.onClaimContract(btn.dataset.contract as string);
        this.showGarage();
      });
    }
    for (const btn of panel.querySelectorAll<HTMLButtonElement>('[data-circuit]')) {
      btn.addEventListener('click', () => {
        this.cb.onClaimCircuit(btn.dataset.circuit as string);
        this.showGarage();
      });
    }
    this.get('#garage-close').addEventListener('click', () =>
      this.get('#garage').classList.add('hidden'),
    );
    this.get('#garage').classList.remove('hidden');
  }

  /** The Vault: hidden shard-tier rewards behind the menu's dim glyph. */
  showVault(): void {
    const p = this.cb.getProfile();
    const panel = this.get('#vault-panel');
    const total = p.shards.length;
    const tiers = VAULT_TIERS.map((v) => {
      const def = COSMETICS.find((c) => c.id === v.cosmetic);
      const claimed = p.unlocked.includes(v.cosmetic);
      const btn = claimed
        ? `<span class="hint">${t('vault.claimed')}</span>`
        : total >= v.shards
          ? `<button class="small" data-vault="${v.id}">${t('vault.claim')}</button>`
          : `<span class="hint">${t('vault.locked', { n: v.shards })}</span>`;
      return `<div class="ach ${claimed ? 'done' : ''}"><b>${def?.label ?? v.cosmetic}</b>
        <span>${v.desc} — ${btn}</span></div>`;
    }).join('');
    panel.innerHTML = `
      <h2>${t('vault.title')}</h2>
      <p class="hint">${t('vault.hint')}</p>
      <div class="stat-line"><span>◇</span><b>${t('vault.progress', { n: total })}</b></div>
      <div class="ach-list">${tiers}</div>
      <button class="small" id="vault-close">${t('vault.close')}</button>
    `;
    for (const btn of panel.querySelectorAll<HTMLButtonElement>('[data-vault]')) {
      btn.addEventListener('click', () => {
        if (this.cb.onClaimVault(btn.dataset.vault as string) !== null) this.showVault();
      });
    }
    panel.querySelector('#vault-close')?.addEventListener('click', () => {
      this.get('#vault').classList.add('hidden');
    });
    this.get('#vault').classList.remove('hidden');
  }

  hideAll(): void {
    this.setVisible(this.menuEl, false);
    this.setVisible(this.hudEl, false);
    this.setVisible(this.pauseEl, false);
    this.setVisible(this.completeEl, false);
    this.get('#garage').classList.add('hidden');
    this.get('#vault').classList.add('hidden');
    this.get('#online').classList.add('hidden');
    this.get('#admin').classList.add('hidden');
    this.get('#login-gate').classList.add('hidden');
  }

  // --- Login gate (multi-window state machine) --------------------------------

  private gateOffline = false;
  /** Carried across the lost-password windows. */
  private gateLost = { user: '', question: '', answer: '' };

  /** The sign-in wall. `offline` reveals the play-offline escape hatch. */
  showLoginGate(offline: boolean): void {
    this.hideAll();
    this.gateOffline = offline;
    this.get('#login-gate').classList.remove('hidden');
    this.renderGate('signin', offline ? t('gate.offline') : '');
  }

  hideLoginGate(): void {
    this.get('#login-gate').classList.add('hidden');
  }

  private renderGate(
    view: 'signin' | 'create' | 'lost-user' | 'lost-answer' | 'lost-new',
    notice = '',
  ): void {
    const host = this.get('#gate-panel');
    const err = `<span class="hint" id="gate-error">${escapeHtml(notice)}</span>`;

    if (view === 'signin') {
      host.innerHTML = `
        <h2>${t('gate.title')}</h2>
        <p class="hint">${t('gate.subtitle')}</p>
        <div class="online-forms">
          <input id="gate-user" type="text" maxlength="20" autocomplete="username" placeholder="${t('gate.username')}">
          <input id="gate-password" type="password" autocomplete="current-password" placeholder="${t('online.password')}">
          <div class="ed-row">
            <button class="small primary" id="gate-login">${t('online.login')}</button>
            <button class="small" id="gate-create">${t('online.register')}</button>
          </div>
          <button class="linklike" id="gate-lost">${t('gate.lost')}</button>
          ${err}
          ${this.gateOffline ? `<button class="small" id="gate-offline">${t('gate.play-offline')}</button>` : ''}
        </div>`;
    } else if (view === 'create') {
      const options = [1, 2, 3, 4, 5, 6].map((i) => `<option>${t(`gate.q${i}`)}</option>`).join('');
      host.innerHTML = `
        <h2>${t('gate.create-title')}</h2>
        <div class="online-forms">
          <input id="gate-user" type="text" maxlength="20" autocomplete="username" placeholder="${t('gate.username')}">
          <input id="gate-password" type="password" autocomplete="new-password" placeholder="${t('online.password')}">
          <label class="hint" for="gate-question">${t('gate.security-q')}</label>
          <select id="gate-question">${options}</select>
          <input id="gate-answer" type="text" maxlength="100" placeholder="${t('gate.answer-ph')}">
          <div class="ed-row">
            <button class="small primary" id="gate-create-go">${t('online.register')}</button>
            <button class="small" id="gate-back">${t('gate.back')}</button>
          </div>
          ${err}
        </div>`;
    } else if (view === 'lost-user') {
      host.innerHTML = `
        <h2>${t('gate.lost')}</h2>
        <div class="online-forms">
          <input id="gate-user" type="text" maxlength="20" placeholder="${t('gate.username')}"
            value="${escapeHtml(this.gateLost.user)}">
          <div class="ed-row">
            <button class="small primary" id="gate-next">${t('gate.next')}</button>
            <button class="small" id="gate-back">${t('gate.back')}</button>
          </div>
          ${err}
        </div>`;
    } else if (view === 'lost-answer') {
      host.innerHTML = `
        <h2>${t('gate.lost')}</h2>
        <div class="online-forms">
          <p class="hint">${t('gate.security-q')}:</p>
          <p><b>${escapeHtml(this.gateLost.question)}</b></p>
          <input id="gate-answer" type="text" maxlength="100" placeholder="${t('gate.answer-ph')}">
          <div class="ed-row">
            <button class="small primary" id="gate-next">${t('gate.next')}</button>
            <button class="small" id="gate-back">${t('gate.back')}</button>
          </div>
          ${err}
        </div>`;
    } else {
      host.innerHTML = `
        <h2>${t('gate.lost')}</h2>
        <div class="online-forms">
          <input id="gate-password" type="password" autocomplete="new-password" placeholder="${t('gate.new-password')}">
          <div class="ed-row">
            <button class="small primary" id="gate-next">${t('gate.save-password')}</button>
            <button class="small" id="gate-back">${t('gate.back')}</button>
          </div>
          ${err}
        </div>`;
    }

    const showErr = (msg: string): void => {
      const el = host.querySelector('#gate-error');
      if (el) el.textContent = msg;
    };
    const val = (sel: string): string =>
      host.querySelector<HTMLInputElement>(sel)?.value.trim() ?? '';
    const raw = (sel: string): string => host.querySelector<HTMLInputElement>(sel)?.value ?? '';
    const onEnter = (sel: string, fn: () => void): void => {
      host.querySelector(sel)?.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).code === 'Enter') fn();
      });
    };

    if (view === 'signin') {
      const login = (): void => {
        showErr('…');
        void this.cb
          .onGateLogin(val('#gate-user'), raw('#gate-password'))
          .then((e) => showErr(e ?? ''));
      };
      host.querySelector('#gate-login')?.addEventListener('click', login);
      onEnter('#gate-password', login);
      host
        .querySelector('#gate-create')
        ?.addEventListener('click', () => this.renderGate('create'));
      host.querySelector('#gate-lost')?.addEventListener('click', () => {
        this.gateLost = { user: '', question: '', answer: '' };
        this.renderGate('lost-user');
      });
      host.querySelector('#gate-offline')?.addEventListener('click', () => this.cb.onGateOffline());
    } else if (view === 'create') {
      const create = (): void => {
        showErr('…');
        const question = host.querySelector<HTMLSelectElement>('#gate-question')?.value ?? '';
        void this.cb
          .onGateRegister(val('#gate-user'), raw('#gate-password'), question, val('#gate-answer'))
          .then((e) => showErr(e ?? ''));
      };
      host.querySelector('#gate-create-go')?.addEventListener('click', create);
      onEnter('#gate-answer', create);
      host.querySelector('#gate-back')?.addEventListener('click', () => this.renderGate('signin'));
    } else if (view === 'lost-user') {
      const next = (): void => {
        showErr('…');
        const user = val('#gate-user');
        void this.cb.onGateQuestion(user).then((result) => {
          if ('error' in result) {
            showErr(result.error);
            return;
          }
          this.gateLost = { user, question: result.question, answer: '' };
          this.renderGate('lost-answer');
        });
      };
      host.querySelector('#gate-next')?.addEventListener('click', next);
      onEnter('#gate-user', next);
      host.querySelector('#gate-back')?.addEventListener('click', () => this.renderGate('signin'));
    } else if (view === 'lost-answer') {
      const next = (): void => {
        showErr('…');
        const answer = val('#gate-answer');
        void this.cb.onGateVerify(this.gateLost.user, answer).then((e) => {
          if (e !== null) {
            showErr(e);
            return;
          }
          this.gateLost.answer = answer;
          this.renderGate('lost-new');
        });
      };
      host.querySelector('#gate-next')?.addEventListener('click', next);
      onEnter('#gate-answer', next);
      host
        .querySelector('#gate-back')
        ?.addEventListener('click', () => this.renderGate('lost-user'));
    } else {
      const save = (): void => {
        showErr('…');
        void this.cb
          .onGateReset(this.gateLost.user, this.gateLost.answer, raw('#gate-password'))
          .then((e) => {
            if (e !== null) {
              showErr(e);
              return;
            }
            this.gateLost = { user: '', question: '', answer: '' };
            this.renderGate('signin', t('gate.reset-done'));
          });
      };
      host.querySelector('#gate-next')?.addEventListener('click', save);
      onEnter('#gate-password', save);
      host
        .querySelector('#gate-back')
        ?.addEventListener('click', () => this.renderGate('lost-answer'));
    }
  }

  /** Menu account line: who is signed in (null hides it). `admin` reveals
   * the admin-console button. */
  setAccount(handle: string | null, admin = false): void {
    this.get('#menu-account').classList.toggle('hidden', handle === null);
    if (handle !== null) {
      this.get('#menu-account-name').textContent = t('online.signed-in', { handle });
    }
    this.get('#btn-admin').classList.toggle('hidden', !admin);
  }

  showAdminPanel(visible: boolean): HTMLElement {
    this.get('#admin').classList.toggle('hidden', !visible);
    return this.get('#admin-panel');
  }

  showOnlinePanel(visible: boolean): HTMLElement {
    this.get('#online').classList.toggle('hidden', !visible);
    return this.get('#online-panel');
  }

  showEditorMode(on: boolean): void {
    if (on) this.hideAll();
  }

  setTesting(on: boolean): void {
    this.get('#testing-badge').classList.toggle('hidden', !on);
  }

  setPractice(on: boolean): void {
    this.get('#btn-practice').textContent = on ? t('pause.practice-on') : t('pause.practice-off');
    this.get('#practice-badge').classList.toggle('hidden', !on);
  }

  flashAttempt(n: number): void {
    this.attemptLabel.textContent = t('hud.attempt', { n });
    this.attemptLabel.classList.add('show');
    window.clearTimeout(this.attemptTimer);
    this.attemptTimer = window.setTimeout(() => {
      this.attemptLabel.classList.remove('show');
    }, 1600);
  }

  private setVisible(el: HTMLElement, visible: boolean): void {
    el.classList.toggle('hidden', !visible);
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);
}
